import { FirestoreService } from '../firestore';
import { AuthService } from '../authService';
import type {
  CommunityEvent,
  EventType,
  EventLocation,
  EventRSVP,
  EventAttendee,
  CreateEventData,
  UpdateEventData,
  SearchEventQuery,
  EventStats,
  EventInteraction,
  EventComment,
  EventAgendaItem,
  CreateAgendaItemData
} from '../../types/communityEvents';

// Collection References
const EVENTS_REF = 'CommunityEvents';
const EVENT_TYPES_REF = 'CommunityEventTypes';
const EVENT_RSVPS_REF = 'CommunityRSVPs';
const EVENT_AGENDA_REF = 'Agenda';
const EVENT_INTERACTIONS_REF = 'CommunityEventInteractions';
const EVENT_COMMENTS_REF = 'CommunityEventComments';

export class CommunityEventsService {
  // Event CRUD Operations
  static async getEvents(): Promise<CommunityEvent[]> {
    console.log('🆕 CommunityEventsService.getEvents() called');
    console.log('🔍 CommunityEventsService - Fetching events from Firestore...');
    
    try {
      const allEvents = await FirestoreService.getDocuments<CommunityEvent>(EVENTS_REF, []);
      console.log('🔍 CommunityEventsService - All events from Firestore:', allEvents.length, 'events');
      
      // Filter and sort in memory
      const filteredEvents = allEvents.filter(event => 
        event.isDeleted === false && event.status === 'published'
      );
      
      console.log('🔍 CommunityEventsService - Filtered events:', filteredEvents.length, 'events');
      
      // Sort by start date (upcoming events first)
      const sortedEvents = filteredEvents.sort((a, b) => 
        new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
      );
      
      console.log('🔍 CommunityEventsService - Sorted events:', sortedEvents.length, 'events');
      console.log('🔍 CommunityEventsService - Event details:', sortedEvents.map(e => ({
        id: e.id,
        title: e.title,
        status: e.status,
        startDate: e.startDate,
        isDeleted: e.isDeleted
      })));
      
      return sortedEvents;
    } catch (error) {
      console.error('❌ CommunityEventsService - Error fetching events:', error);
      throw error;
    }
  }

  static async getEventById(eventId: string): Promise<CommunityEvent | null> {
    try {
      const event = await FirestoreService.getDocument<CommunityEvent>(EVENTS_REF, eventId);
      if (event && !event.isDeleted) {
        return event;
      }
      return null;
    } catch (error) {
      console.error('Error fetching event by ID:', error);
      throw error;
    }
  }

  static async createEvent(eventData: CreateEventData): Promise<string> {
    try {
      console.log('🆕 CommunityEventsService.createEvent() called');
      console.log('📝 Event data received:', eventData);
      
      // Get current user information
      const currentUser = await AuthService.getCurrentUser();
      if (!currentUser) {
        throw new Error('User must be authenticated to create an event');
      }
      
      console.log('👤 Current user:', {
        id: currentUser.id,
        firstName: currentUser.firstName,
        lastName: currentUser.lastName
      });
      
      // Comprehensive validation
      this.validateEventData(eventData);
      
      const now = new Date().toISOString();
      
      // Generate a unique event ID
      const eventId = `event_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      console.log('📋 Event type ID:', eventData.communityEventTypeId);
      
      // Prepare the event document with all required fields
      const eventDocument: any = {
        // Basic event information
        title: eventData.title,
        description: eventData.description,
        communityEventTypeId: eventData.communityEventTypeId, // String ID reference
        startDate: eventData.startDate,
        endDate: eventData.endDate,
        // Location fields (flattened)
        locationType: eventData.locationType,
        address: eventData.address || '',
        city: eventData.city || '',
        country: eventData.country || '',
        latitude: eventData.latitude || 0.0,
        longitude: eventData.longitude || 0.0,
        venueName: eventData.venueName || '',
        isOnline: eventData.isOnline,
        meetingLink: eventData.meetingLink || '',
        currentAttendees: 0,
        isRegistrationRequired: eventData.isRegistrationRequired,
        isFree: eventData.isFree,
        currency: eventData.currency || 'SAR',
        organizerId: currentUser.id,
        organizerName: `${currentUser.firstName} ${currentUser.lastName}`.trim(),
        tags: eventData.tags || [],
        status: 'published',
        
        // Statistics
        rsvpCount: 0,
        likeCount: 0,
        viewCount: 0,
        
        // Metadata
        createdAt: now,
        updatedAt: now,
        createdBy: currentUser.id,
        isDeleted: false,
        isArchived: false
      };

      // Only add optional fields if they have values
      if (eventData.maxAttendees !== undefined) {
        eventDocument.maxAttendees = eventData.maxAttendees;
      }
      
      if (eventData.registrationDeadline) {
        eventDocument.registrationDeadline = eventData.registrationDeadline;
      }
      
      if (eventData.price !== undefined) {
        eventDocument.price = eventData.price;
      }
      
      if (currentUser.photoURL) {
        eventDocument.organizerPhotoURL = currentUser.photoURL;
      }
      
      if (eventData.imageURL) {
        eventDocument.imageURL = eventData.imageURL;
      }
      
      if (eventData.requirements) {
        eventDocument.requirements = eventData.requirements;
      }
      
      console.log('📄 Event document prepared:', eventDocument);
      
      // Final safety check - remove any undefined values that might have slipped through
      const finalDocument: any = {
        id: eventId,
        ...eventDocument
      };
      
      // Remove any undefined values
      Object.keys(finalDocument).forEach(key => {
        if (finalDocument[key] === undefined) {
          console.log(`⚠️ Removing undefined field: ${key}`);
          delete finalDocument[key];
        }
      });
      
      console.log('📄 Final document for Firestore:', finalDocument);
      console.log('🔍 Checking for undefined values:', Object.entries(finalDocument).filter(([_, value]) => value === undefined));
      
      // Add the event to Firestore with the specific path /CommunityEvents/{event-id}
      const addedEventId = await FirestoreService.addDocument(EVENTS_REF, finalDocument);
      
      console.log('✅ Event created successfully in Firestore:');
      console.log('   - Collection: CommunityEvents');
      console.log('   - Document ID:', addedEventId);
      console.log('   - Event ID:', eventId);
      console.log('   - Title:', eventData.title);
      console.log('   - Organizer:', eventDocument.organizerName);
      console.log('   - Start Date:', eventData.startDate);
      console.log('   - Location Type:', eventData.isOnline ? 'Online' : 'Physical');
      
      return addedEventId;
    } catch (error) {
      console.error('❌ Error creating event:', error);
      throw error;
    }
  }

  static async updateEvent(eventId: string, eventData: UpdateEventData): Promise<void> {
    try {
      await FirestoreService.updateDocument(EVENTS_REF, eventId, {
        ...eventData,
        updatedAt: new Date().toISOString(),
        updatedBy: 'current-user-id' // This should be replaced with actual user ID
      });
      
      console.log('✅ Event updated successfully:', eventId);
    } catch (error) {
      console.error('Error updating event:', error);
      throw error;
    }
  }

  static async deleteEvent(eventId: string): Promise<void> {
    try {
      await FirestoreService.updateDocument(EVENTS_REF, eventId, {
        isDeleted: true,
        updatedAt: new Date().toISOString(),
        updatedBy: 'current-user-id' // This should be replaced with actual user ID
      });
      
      console.log('✅ Event deleted successfully:', eventId);
    } catch (error) {
      console.error('Error deleting event:', error);
      throw error;
    }
  }

  // Event Types
  static async getEventTypes(): Promise<EventType[]> {
    try {
      // This will be replaced with cached SystemPresetData from SystemPresetContext
      // For now, return empty array - the actual implementation will use cached presets
      console.log('📋 getEventTypes() called - will use cached SystemPresetData');
      return [];
    } catch (error) {
      console.error('Error fetching event types:', error);
      throw error;
    }
  }

  // RSVP Operations
  static async rsvpToEvent(eventId: string, userId: string, status: 'attending' | 'not_attending' | 'maybe' = 'attending'): Promise<void> {
    try {
      const now = new Date().toISOString();
      const rsvpId = `${eventId}_${userId}`;
      
      await FirestoreService.addDocument(EVENT_RSVPS_REF, {
        id: rsvpId,
        eventId,
        userId,
        status,
        registeredAt: now,
        isWaitlisted: false,
        createdAt: now,
        updatedAt: now,
        createdBy: userId,
        isDeleted: false,
        isArchived: false
      });

      // Update event attendee count
      await this.updateEventAttendeeCount(eventId);
      
      console.log('✅ RSVP successful:', { eventId, userId, status });
    } catch (error) {
      console.error('Error RSVPing to event:', error);
      throw error;
    }
  }

  static async cancelRSVP(eventId: string, userId: string): Promise<void> {
    try {
      const rsvpId = `${eventId}_${userId}`;
      await FirestoreService.updateDocument(EVENT_RSVPS_REF, rsvpId, {
        isDeleted: true,
        updatedAt: new Date().toISOString()
      });

      // Update event attendee count
      await this.updateEventAttendeeCount(eventId);
      
      console.log('✅ RSVP cancelled:', { eventId, userId });
    } catch (error) {
      console.error('Error cancelling RSVP:', error);
      throw error;
    }
  }

  static async getEventAttendees(eventId: string): Promise<EventAttendee[]> {
    try {
      const rsvps = await FirestoreService.getDocuments<EventRSVP>(EVENT_RSVPS_REF, [
        FirestoreService.where('eventId', '==', eventId),
        FirestoreService.where('isDeleted', '==', false)
      ]);

      return rsvps.map(rsvp => ({
        id: rsvp.id,
        userId: rsvp.userId,
        userName: rsvp.userName,
        userPhotoURL: rsvp.userPhotoURL,
        userTitle: rsvp.userTitle,
        status: rsvp.status,
        registeredAt: rsvp.registeredAt,
        notes: rsvp.notes
      }));
    } catch (error) {
      console.error('Error fetching event attendees:', error);
      throw error;
    }
  }

  // Search and Filtering
  static async searchEvents(query: SearchEventQuery): Promise<CommunityEvent[]> {
    try {
      let events = await this.getEvents();
      
      // Apply filters
      if (query.communityEventTypeId) {
        events = events.filter(event => event.communityEventTypeId === query.communityEventTypeId);
      }
      
      if (query.startDate) {
        events = events.filter(event => new Date(event.startDate) >= new Date(query.startDate!));
      }
      
      if (query.endDate) {
        events = events.filter(event => new Date(event.startDate) <= new Date(query.endDate!));
      }
      
      if (query.isOnline !== undefined) {
        events = events.filter(event => event.isOnline === query.isOnline);
      }
      
      if (query.isFree !== undefined) {
        events = events.filter(event => event.isFree === query.isFree);
      }
      
      if (query.priceMin !== undefined) {
        events = events.filter(event => event.price && event.price >= query.priceMin!);
      }
      
      if (query.priceMax !== undefined) {
        events = events.filter(event => event.price && event.price <= query.priceMax!);
      }
      
      if (query.organizerId) {
        events = events.filter(event => event.organizerId === query.organizerId);
      }
      
      if (query.tags && query.tags.length > 0) {
        events = events.filter(event => 
          query.tags!.some(tag => event.tags.includes(tag))
        );
      }
      
      if (query.status) {
        events = events.filter(event => event.status === query.status);
      }
      
      // Apply text search
      if (query.query) {
        const searchTerm = query.query.toLowerCase();
        events = events.filter(event => 
          event.title.toLowerCase().includes(searchTerm) ||
          event.description.toLowerCase().includes(searchTerm) ||
          event.organizerName.toLowerCase().includes(searchTerm) ||
          event.tags.some(tag => tag.toLowerCase().includes(searchTerm))
        );
      }
      
      // Apply pagination
      if (query.offset) {
        events = events.slice(query.offset);
      }
      
      if (query.limit) {
        events = events.slice(0, query.limit);
      }
      
      return events;
    } catch (error) {
      console.error('Error searching events:', error);
      throw error;
    }
  }

  // Event Interactions
  static async likeEvent(eventId: string, userId: string): Promise<void> {
    try {
      const interactionId = `${eventId}_${userId}_like`;
      await FirestoreService.addDocument(EVENT_INTERACTIONS_REF, {
        id: interactionId,
        eventId,
        userId,
        type: 'like',
        createdAt: new Date().toISOString()
      });

      // Update event like count
      await this.updateEventLikeCount(eventId);
      
      console.log('✅ Event liked:', { eventId, userId });
    } catch (error) {
      console.error('Error liking event:', error);
      throw error;
    }
  }

  static async shareEvent(eventId: string, userId: string): Promise<void> {
    try {
      const interactionId = `${eventId}_${userId}_share_${Date.now()}`;
      await FirestoreService.addDocument(EVENT_INTERACTIONS_REF, {
        eventId,
        userId,
        type: 'share',
        createdAt: new Date().toISOString()
      });
      
      console.log('✅ Event shared:', { eventId, userId });
    } catch (error) {
      console.error('Error sharing event:', error);
      throw error;
    }
  }

  static async reportEvent(eventId: string, userId: string, reason: string): Promise<void> {
    try {
      const interactionId = `${eventId}_${userId}_report_${Date.now()}`;
      await FirestoreService.addDocument(EVENT_INTERACTIONS_REF, {
        eventId,
        userId,
        type: 'report',
        metadata: { reason },
        createdAt: new Date().toISOString()
      });
      
      console.log('✅ Event reported:', { eventId, userId, reason });
    } catch (error) {
      console.error('Error reporting event:', error);
      throw error;
    }
  }

  // Event Comments
  static async addEventComment(eventId: string, content: string, userId: string, userName: string): Promise<string> {
    try {
      const commentId = await FirestoreService.addDocument(EVENT_COMMENTS_REF, {
        eventId,
        content,
        authorId: userId,
        authorName: userName,
        likeCount: 0,
        isEdited: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: userId,
        isDeleted: false,
        isArchived: false
      });
      
      console.log('✅ Event comment added:', commentId);
      return commentId;
    } catch (error) {
      console.error('Error adding event comment:', error);
      throw error;
    }
  }

  static async getEventComments(eventId: string): Promise<EventComment[]> {
    try {
      const comments = await FirestoreService.getDocuments<EventComment>(EVENT_COMMENTS_REF, [
        FirestoreService.where('eventId', '==', eventId),
        FirestoreService.where('isDeleted', '==', false)
      ]);

      return comments.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    } catch (error) {
      console.error('Error fetching event comments:', error);
      throw error;
    }
  }

  // Statistics
  static async getEventStats(): Promise<EventStats> {
    try {
      const events = await this.getEvents();
      
      const stats: EventStats = {
        totalEvents: events.length,
        publishedEvents: events.filter(e => e.status === 'published').length,
        draftEvents: events.filter(e => e.status === 'draft').length,
        cancelledEvents: events.filter(e => e.status === 'cancelled').length,
        completedEvents: events.filter(e => e.status === 'completed').length,
        totalAttendees: events.reduce((sum, event) => sum + event.currentAttendees, 0),
        averageAttendees: 0,
        popularEventTypes: []
      };

      stats.averageAttendees = stats.totalEvents > 0 ? stats.totalAttendees / stats.totalEvents : 0;

      // Calculate popular event types
      const eventTypeCounts = events.reduce((acc, event) => {
        const typeId = event.communityEventTypeId;
        acc[typeId] = (acc[typeId] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      stats.popularEventTypes = Object.entries(eventTypeCounts)
        .map(([typeId, count]) => ({
          eventTypeId: typeId,
          eventTypeName: 'Unknown', // Will be resolved from cached presets
          count
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      return stats;
    } catch (error) {
      console.error('Error fetching event stats:', error);
      throw error;
    }
  }

  // Helper Methods
  private static async updateEventAttendeeCount(eventId: string): Promise<void> {
    try {
      const attendees = await this.getEventAttendees(eventId);
      const attendingCount = attendees.filter(a => a.status === 'attending').length;
      
      await FirestoreService.updateDocument(EVENTS_REF, eventId, {
        currentAttendees: attendingCount,
        rsvpCount: attendees.length,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error updating event attendee count:', error);
    }
  }

  private static async updateEventLikeCount(eventId: string): Promise<void> {
    try {
      const likes = await FirestoreService.getDocuments<EventInteraction>(EVENT_INTERACTIONS_REF, [
        FirestoreService.where('eventId', '==', eventId),
        FirestoreService.where('type', '==', 'like')
      ]);

      await FirestoreService.updateDocument(EVENTS_REF, eventId, {
        likeCount: likes.length,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error updating event like count:', error);
    }
  }

  // Agenda Management Functions
  static async createEventWithAgenda(eventData: CreateEventData, agendaItems: CreateAgendaItemData[]): Promise<string> {
    try {
      console.log('🆕 CommunityEventsService.createEventWithAgenda() called');
      console.log('📝 Event data received:', eventData);
      console.log('📋 Agenda items received:', agendaItems);

      // First create the event
      const eventId = await this.createEvent(eventData);
      
      // Then create agenda items in sub-collection
      if (agendaItems && agendaItems.length > 0) {
        await this.createAgendaItems(eventId, agendaItems);
      }

      console.log('✅ Event with agenda created successfully');
      return eventId;
    } catch (error) {
      console.error('❌ Error creating event with agenda:', error);
      throw error;
    }
  }

  static async createAgendaItems(eventId: string, agendaItems: CreateAgendaItemData[]): Promise<void> {
    try {
      console.log('📋 Creating agenda items for event:', eventId);
      
      const currentUser = await AuthService.getCurrentUser();
      if (!currentUser) {
        throw new Error('User must be authenticated to create agenda items');
      }

      const now = new Date().toISOString();
      const agendaCollectionPath = `${EVENTS_REF}/${eventId}/${EVENT_AGENDA_REF}`;

      // Create agenda items in parallel
      const agendaPromises = agendaItems.map(async (item, index) => {
        const agendaItemId = `agenda_${Date.now()}_${index}`;
        
        const agendaItemDocument: any = {
          id: agendaItemId,
          title: item.title,
          description: item.description || '',
          startTime: item.startTime,
          endTime: item.endTime,
          speakerId: item.speakerId || '',
          speakerName: item.speakerName || '',
          speakerTitle: item.speakerTitle || '',
          createdAt: now,
          updatedAt: now,
          createdBy: currentUser.id,
          isDeleted: false,
          isArchived: false
        };

        // Remove undefined values
        Object.keys(agendaItemDocument).forEach(key => {
          if (agendaItemDocument[key] === undefined) {
            delete agendaItemDocument[key];
          }
        });

        return FirestoreService.addDocument(agendaCollectionPath, agendaItemDocument);
      });

      await Promise.all(agendaPromises);
      console.log('✅ Agenda items created successfully');
    } catch (error) {
      console.error('❌ Error creating agenda items:', error);
      throw error;
    }
  }

  static async getEventAgenda(eventId: string): Promise<EventAgendaItem[]> {
    try {
      const agendaCollectionPath = `${EVENTS_REF}/${eventId}/${EVENT_AGENDA_REF}`;
      const agendaItems = await FirestoreService.getDocuments<EventAgendaItem>(agendaCollectionPath, []);
      
      // Filter out deleted items and sort by start time
      return agendaItems
        .filter(item => !item.isDeleted)
        .sort((a, b) => a.startTime.localeCompare(b.startTime));
    } catch (error) {
      console.error('Error fetching event agenda:', error);
      throw error;
    }
  }

  static async addAgendaItem(eventId: string, agendaItem: CreateAgendaItemData): Promise<string> {
    try {
      const currentUser = await AuthService.getCurrentUser();
      if (!currentUser) {
        throw new Error('User must be authenticated to add agenda items');
      }

      const now = new Date().toISOString();
      const agendaItemId = `agenda_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const agendaCollectionPath = `${EVENTS_REF}/${eventId}/${EVENT_AGENDA_REF}`;

      const agendaItemDocument: any = {
        id: agendaItemId,
        title: agendaItem.title,
        description: agendaItem.description || '',
        startTime: agendaItem.startTime,
        endTime: agendaItem.endTime,
        speakerId: agendaItem.speakerId || '',
        speakerName: agendaItem.speakerName || '',
        speakerTitle: agendaItem.speakerTitle || '',
        createdAt: now,
        updatedAt: now,
        createdBy: currentUser.id,
        isDeleted: false,
        isArchived: false
      };

      // Remove undefined values
      Object.keys(agendaItemDocument).forEach(key => {
        if (agendaItemDocument[key] === undefined) {
          delete agendaItemDocument[key];
        }
      });

      return await FirestoreService.addDocument(agendaCollectionPath, agendaItemDocument);
    } catch (error) {
      console.error('Error adding agenda item:', error);
      throw error;
    }
  }

  static async updateAgendaItem(eventId: string, agendaItemId: string, updates: Partial<CreateAgendaItemData>): Promise<void> {
    try {
      const currentUser = await AuthService.getCurrentUser();
      if (!currentUser) {
        throw new Error('User must be authenticated to update agenda items');
      }

      const now = new Date().toISOString();
      const agendaCollectionPath = `${EVENTS_REF}/${eventId}/${EVENT_AGENDA_REF}`;

      const updateData: any = {
        ...updates,
        updatedAt: now,
        updatedBy: currentUser.id
      };

      // Remove undefined values
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === undefined) {
          delete updateData[key];
        }
      });

      await FirestoreService.updateDocument(agendaCollectionPath, agendaItemId, updateData);
    } catch (error) {
      console.error('Error updating agenda item:', error);
      throw error;
    }
  }

  static async deleteAgendaItem(eventId: string, agendaItemId: string): Promise<void> {
    try {
      const currentUser = await AuthService.getCurrentUser();
      if (!currentUser) {
        throw new Error('User must be authenticated to delete agenda items');
      }

      const now = new Date().toISOString();
      const agendaCollectionPath = `${EVENTS_REF}/${eventId}/${EVENT_AGENDA_REF}`;

      await FirestoreService.updateDocument(agendaCollectionPath, agendaItemId, {
        isDeleted: true,
        updatedAt: now,
        updatedBy: currentUser.id
      });
    } catch (error) {
      console.error('Error deleting agenda item:', error);
      throw error;
    }
  }

  // Validation helper method
  private static validateEventData(eventData: CreateEventData): void {
    const errors: string[] = [];

    // Required fields validation
    if (!eventData.title || eventData.title.trim().length === 0) {
      errors.push('Event title is required');
    } else if (eventData.title.length > 200) {
      errors.push('Event title must be less than 200 characters');
    }

    if (!eventData.description || eventData.description.trim().length === 0) {
      errors.push('Event description is required');
    } else if (eventData.description.length > 2000) {
      errors.push('Event description must be less than 2000 characters');
    }

    if (!eventData.communityEventTypeId) {
      errors.push('Event type is required');
    }

    if (!eventData.startDate) {
      errors.push('Start date is required');
    } else {
      const startDate = new Date(eventData.startDate);
      if (isNaN(startDate.getTime())) {
        errors.push('Invalid start date format');
      } else if (startDate < new Date()) {
        errors.push('Start date cannot be in the past');
      }
    }

    if (!eventData.endDate) {
      errors.push('End date is required');
    } else {
      const endDate = new Date(eventData.endDate);
      const startDate = new Date(eventData.startDate);
      if (isNaN(endDate.getTime())) {
        errors.push('Invalid end date format');
      } else if (endDate <= startDate) {
        errors.push('End date must be after start date');
      }
    }

    // Location validation
    if (eventData.locationType === 'physical') {
      if (!eventData.address || eventData.address.trim().length === 0) {
        errors.push('Address is required for physical events');
      }
      if (!eventData.city || eventData.city.trim().length === 0) {
        errors.push('City is required for physical events');
      }
    } else if (eventData.locationType === 'online') {
      if (!eventData.meetingLink || eventData.meetingLink.trim().length === 0) {
        errors.push('Meeting link is required for online events');
      }
    }

    // Price validation
    if (!eventData.isFree && eventData.price !== undefined) {
      if (eventData.price < 0) {
        errors.push('Price cannot be negative');
      }
      if (eventData.price > 10000) {
        errors.push('Price cannot exceed 10,000 SAR');
      }
    }

    // Attendees validation
    if (eventData.maxAttendees !== undefined) {
      if (eventData.maxAttendees < 1) {
        errors.push('Maximum attendees must be at least 1');
      }
      if (eventData.maxAttendees > 1000) {
        errors.push('Maximum attendees cannot exceed 1000');
      }
    }

    // Registration deadline validation
    if (eventData.registrationDeadline) {
      const deadline = new Date(eventData.registrationDeadline);
      const startDate = new Date(eventData.startDate);
      if (isNaN(deadline.getTime())) {
        errors.push('Invalid registration deadline format');
      } else if (deadline >= startDate) {
        errors.push('Registration deadline must be before the event start date');
      }
    }

    // Tags validation
    if (eventData.tags && eventData.tags.length > 10) {
      errors.push('Maximum 10 tags allowed');
    }

    if (eventData.tags) {
      for (const tag of eventData.tags) {
        if (tag.length > 50) {
          errors.push('Each tag must be less than 50 characters');
          break;
        }
      }
    }

    // Throw error if validation fails
    if (errors.length > 0) {
      throw new Error(`Validation failed: ${errors.join(', ')}`);
    }
  }
}
